import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, FileText } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';

interface TradeRow {
  id: string;
  action: string;
  ticker: string | null;
  side: string | null;
  count: number | null;
  priceDollars: string | null;
  kalshiOrderId: string | null;
  status: string;
  credentialScope: 'org' | 'user' | string;
  errorMessage: string | null;
  createdAt: string;
}

export function MyTrades() {
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/trader/trades?limit=200');
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to load');
      } else {
        setTrades(data.trades ?? []);
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-muted-foreground" />
          <h2 className="text-lg font-semibold">My Trades</h2>
          <span className="text-muted-foreground text-xs">({trades.length})</span>
        </div>
        <button onClick={fetchTrades} title="Refresh"
          className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </div>
      ) : trades.length === 0 ? (
        <div className="bg-card border-border rounded-lg border p-6 text-center">
          <p className="text-muted-foreground text-sm">No trades yet.</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Your activity will appear here once you place an order.
          </p>
        </div>
      ) : (
        <div className="bg-card border-border rounded-lg border">
          <table className="w-full text-xs">
            <thead className="bg-muted/30 sticky top-0">
              <tr className="text-muted-foreground border-border border-b">
                <th className="px-3 py-2 text-left font-medium">Time</th>
                <th className="px-3 py-2 text-left font-medium">Action</th>
                <th className="px-3 py-2 text-left font-medium">Ticker</th>
                <th className="px-3 py-2 text-left font-medium">Side</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">Price</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Key</th>
                <th className="px-3 py-2 text-left font-medium">Order ID</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.id} className="border-border/40 border-b last:border-0">
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {new Date(t.createdAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5">{t.action.replace('_', ' ')}</td>
                  <td className="px-3 py-1.5 font-mono">{t.ticker ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.side ?? '—'}</td>
                  <td className="px-3 py-1.5 text-right">{t.count ?? 0}</td>
                  <td className="px-3 py-1.5 text-right">
                    {t.priceDollars ? `$${Number(t.priceDollars).toFixed(2)}` : '—'}
                  </td>
                  <td className={`px-3 py-1.5 ${t.status === 'rejected' ? 'text-red-400' : 'text-emerald-500'}`}>
                    {t.status}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${
                      t.credentialScope === 'org'
                        ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                        : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    }`}>
                      {t.credentialScope}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">
                    {t.kalshiOrderId ? `${t.kalshiOrderId.slice(0, 10)}…` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
